/**
 * dto.ts — HTTP request Zod contracts for the Cuentas a Pagar module (Work
 * Unit 4, PR4). Response shapes are plain mapper functions in the route
 * files (same convention as `stock.routes.ts`'s `toAdminResponse` / mold
 * `compras.routes.ts`) — this file covers the request-body contracts that
 * MUST be Zod per the http-routes spec's binding constraint, plus the two
 * literal-shape response contracts the spec pins down by name
 * (`ComprobanteLoteResponseSchema`, already in comprobante-lote-parser.ts).
 *
 * Spec: openspec/changes/add-cuentas-pagar/specs/cuentas-pagar-http-routes/spec.md
 * Design: openspec/changes/add-cuentas-pagar/design.md
 */
import { z } from 'zod';
import { CurrencyCodeSchema } from '@suite/contracts-kernel';
import {
  ComprobanteTipoSchema,
  RespaldoTipoSchema,
  DestinoCostoTipoSchema,
  PercepcionTipoSchema,
  GananciasConceptoSchema,
} from './vocab.js';

/** One perception of the invoice: its type and amount (magnitude; the sign follows `tipo` like the total). */
export const PercepcionInputSchema = z.object({
  tipo: PercepcionTipoSchema,
  monto: z.number({ invalid_type_error: 'Ingresá el monto de la percepción.' }).positive('Ingresá el monto de la percepción.'),
});
export type PercepcionInput = z.infer<typeof PercepcionInputSchema>;

/** Sum of the perceptions (2 decimals). */
export function totalPercepciones(percepciones: readonly { monto: number }[] | undefined): number {
  return Math.round((percepciones ?? []).reduce((s, p) => s + p.monto, 0) * 100) / 100;
}

// ---------------------------------------------------------------------------
// APR-1 — Alta de Comprobante (individual)
// ---------------------------------------------------------------------------

/**
 * `llaveExterna` shape (AP-1.4) is chosen by which of the three optional
 * inputs is supplied — the use case computes the real key server-side via
 * `makeKey`/`makeNaturalKey` (D9):
 *   - `nComp` provided            -> llaveFuente = 'n_comp'
 *   - `origenSistemaId` provided  -> llaveFuente = 'origen_sistema'
 *   - neither                     -> llaveFuente = 'natural' (requires
 *     `puntoVenta` + `numero`, mirrors `chk_comprobante_llave_natural`)
 */
export const ComprobanteCreateSchema = z
  .object({
    supplierId: z.string().uuid(),
    tipo: ComprobanteTipoSchema,
    letra: z.string().optional(),
    puntoVenta: z.string().optional(),
    numero: z.string().optional(),
    /** Raw AFIP N_COMP string (e.g. "A0000100012345"), parsed via `parseNComp`. */
    nComp: z.string().optional(),
    /** Pre-existing external system id — `origen='importado'` via a single-row alta. */
    origenSistemaId: z.string().optional(),
    moneda: CurrencyCodeSchema,
    tipoCambio: z.number().positive().optional(),
    /** Magnitude, ALWAYS positive — the sign is derived server-side from `tipo` (chk_comprobante_signo). */
    montoTotal: z.number().positive(),
    /**
     * AP-1.1b — net amount, REQUIRED on the individual alta. Magnitude like
     * `montoTotal` (sign derived server-side from `tipo`), never above the
     * total. VAT is derived (total − neto), never stored or sent.
     */
    montoNeto: z.number({ required_error: 'Ingresá el neto del comprobante.', invalid_type_error: 'Ingresá el neto del comprobante.' }).nonnegative(),
    fechaEmision: z.string().refine((s) => !Number.isNaN(new Date(s).getTime()), 'fechaEmision inválida'),
    /** Manual override of the suggested due date (AP-10). */
    fechaVencimiento: z
      .string()
      .refine((s) => !Number.isNaN(new Date(s).getTime()), 'fechaVencimiento inválida')
      .optional(),
    respaldoTipo: RespaldoTipoSchema.optional(),
    respaldoId: z.string().uuid().optional(),
    /** The uploaded file (`POST /files`) — required with `respaldoTipo: 'certificacion'`, the signed certification. */
    adjuntoFileId: z.string().uuid().optional(),
    destinoCostoTipo: DestinoCostoTipoSchema.optional(),
    destinoCostoId: z.string().uuid().optional(),
    /**
     * cxp-distribucion-cc-por-linea-oc — only meaningful with
     * `respaldoTipo: 'orden_compra'`: the OC's po_line ids the tesorero left
     * ticked (default: all of them). Omitted -> the use case distributes over
     * every non-annulled line of the OC. `min(1)` mirrors the product rule
     * "at least one line must stay ticked" — an explicit empty array is
     * rejected here, before it ever reaches the use case.
     */
    lineasOc: z.array(z.string().uuid()).min(1).optional(),
    /** Perceptions charged on the invoice, at most one per type. They add to the total (neto + IVA + percepciones). */
    percepciones: z.array(PercepcionInputSchema).max(3).optional(),
    /** "Detalle de pago": what is being paid, free text. */
    detallePago: z.string().trim().max(500, 'El detalle de pago puede tener hasta 500 caracteres.').optional(),
    /** Ganancias concept (migration 0044); omitted = not informed, the retention counts it as servicios. */
    gananciasConcepto: GananciasConceptoSchema.optional(),
    /** Magnitude of the bienes de cambio part of the neto; only and always with `gananciasConcepto: 'mixto'`. */
    netoBienes: z.number({ invalid_type_error: 'Ingresá el monto de bienes de cambio.' }).nonnegative().optional(),
  })
  .refine((v) => v.nComp !== undefined || v.origenSistemaId !== undefined || (!!v.puntoVenta && !!v.numero), {
    message: 'puntoVenta y numero son requeridos para la llave natural (chk_comprobante_llave_natural).',
    path: ['puntoVenta'],
  })
  .refine((v) => v.montoNeto <= v.montoTotal, {
    message: 'El neto no puede superar al total.',
    path: ['montoNeto'],
  })
  .refine((v) => v.respaldoTipo !== 'certificacion' || v.adjuntoFileId !== undefined, {
    message: 'Adjuntá la certificación.',
    path: ['adjuntoFileId'],
  })
  .refine((v) => v.adjuntoFileId === undefined || v.respaldoTipo === 'certificacion', {
    message: 'El adjunto va solo con el respaldo «Certificación».',
    path: ['adjuntoFileId'],
  })
  .refine((v) => new Set((v.percepciones ?? []).map((p) => p.tipo)).size === (v.percepciones ?? []).length, {
    message: 'Una percepción por tipo.',
    path: ['percepciones'],
  })
  .refine((v) => Math.round((v.montoNeto + totalPercepciones(v.percepciones)) * 100) <= Math.round(v.montoTotal * 100), {
    message: 'El neto más las percepciones no puede superar al total.',
    path: ['percepciones'],
  })
  .refine((v) => (v.gananciasConcepto === 'mixto') === (v.netoBienes !== undefined), {
    message: 'Una factura mixta lleva el monto de bienes de cambio, y solo ella.',
    path: ['netoBienes'],
  })
  .refine((v) => v.netoBienes === undefined || v.netoBienes <= v.montoNeto, {
    message: 'Los bienes de cambio no pueden superar al neto.',
    path: ['netoBienes'],
  })
  .refine((v) => !v.destinoCostoId || v.destinoCostoTipo === 'centro_costo', {
    message: 'destinoCostoTipo debe ser centro_costo cuando se informa destinoCostoId.',
    path: ['destinoCostoTipo'],
  });

export type ComprobanteCreateInput = z.infer<typeof ComprobanteCreateSchema>;

// ---------------------------------------------------------------------------
// APR-2 — Carga en lote (saldo inicial / apertura)
// ---------------------------------------------------------------------------

/**
 * Task 6.4 (tasks.md) — open risk carried from design.md: no real row count
 * for EDHIPSA's opening load exists yet, so this is a documented, easily
 * adjustable placeholder, NOT a confirmed business limit. One
 * `runInTenantTx` per CALL (over that call's `valid` set) is the design
 * position; the caller splits a larger file into multiple calls.
 */
export const MAX_LOTE_ROWS = 500;

export const ComprobanteLoteCreateSchema = z.object({
  rows: z.array(z.unknown()).min(1, 'El lote debe tener al menos una fila.').max(MAX_LOTE_ROWS, `El lote no puede superar ${MAX_LOTE_ROWS} filas por llamada.`),
});

// ---------------------------------------------------------------------------
// APR-3 — Imputar (incluye registrar el pago cuando corresponde, D2)
// ---------------------------------------------------------------------------

const AplicacionInputSchema = z.object({
  comprobanteDestinoId: z.string().uuid(),
  montoAplicado: z.number().positive(),
  moneda: CurrencyCodeSchema,
  tipoCambio: z.number().positive().optional(),
});

/**
 * APR-3 / APL-11 (retenciones-en-el-pago) — only applies a `emision_nc` / `emision_nd` movement against
 * comprobantes. The cash payment (`pagoNuevo`) is gone: a `pago` / `retencion` movement is only written by
 * paying a comprobante (`PagarComprobanteUseCase`).
 */
export const ImputacionCreateSchema = z.object({
  movimientoOrigenId: z.string().uuid(),
  aplicaciones: z.array(AplicacionInputSchema).min(1, 'Debe informar al menos una aplicación.'),
});

export type ImputacionCreateInput = z.infer<typeof ImputacionCreateSchema>;

// ---------------------------------------------------------------------------
// APR-4 — Anular
// ---------------------------------------------------------------------------

export const AnularMovimientoSchema = z.object({
  motivo: z.string(),
});

// ---------------------------------------------------------------------------
// APR-6 — Configuración de AP y catálogo de retenciones
// ---------------------------------------------------------------------------

export const ApConfigUpdateSchema = z.object({
  fechaCorteSaldoInicial: z
    .string()
    .refine((s) => !Number.isNaN(new Date(s).getTime()), 'fechaCorteSaldoInicial inválida')
    .nullable()
    .optional(),
  plazoPagoPorDefectoDias: z.number().int().nonnegative().nullable().optional(),
  /** AP-9.1/AP-9.2, D8 — null means "use the DEFAULT_TOLERANCIA_MONTO_OC_PCT (2%) default". */
  toleranciaMontoOcPct: z.number().nonnegative().max(100).nullable().optional(),
});

export type ApConfigUpdateInput = z.infer<typeof ApConfigUpdateSchema>;

// ---------------------------------------------------------------------------
// Error codes — AP-specific, same pattern as COMPRAS_ERROR_CODES.
// ---------------------------------------------------------------------------

export const AP_ERROR_CODES = {
  /** AP-1.4 — uq_comprobante_llave_externa. */
  COMPROBANTE_DUPLICADO: 'AP_COMPROBANTE_DUPLICADO',
  /** D8 — company.cuit IS NULL, auto-provisioning cannot resolve a payerLegalEntityId. */
  PAYER_CUIT_MISSING: 'AP_PAYER_CUIT_MISSING',
  /** AP-4.2 / validateImputacion excede_pendiente. */
  IMPUTACION_EXCEDE_PENDIENTE: 'AP_IMPUTACION_EXCEDE_PENDIENTE',
  /** AP-4.2 / validateImputacion excede_origen. */
  IMPUTACION_EXCEDE_ORIGEN: 'AP_IMPUTACION_EXCEDE_ORIGEN',
  /** AP-4.2 / validateImputacion tc_faltante. */
  IMPUTACION_TC_FALTANTE: 'AP_IMPUTACION_TC_FALTANTE',
  IMPUTACION_TC_NO_POSITIVO: 'AP_IMPUTACION_TC_NO_POSITIVO',
  /** D10 — uq_movimiento_anulacion / anular()'s ya_anulado. */
  MOVIMIENTO_YA_ANULADO: 'AP_MOVIMIENTO_YA_ANULADO',
  /** anular()'s motivo_vacio. */
  MOTIVO_VACIO: 'AP_MOTIVO_VACIO',
  /** T7 (odd/tasks/cuentas-pagar-detalle-imputacion-reporte.md) — GetPagoDetalleUseCase, movimiento.tipo !== 'pago'. */
  MOVIMIENTO_NO_ES_PAGO: 'AP_MOVIMIENTO_NO_ES_PAGO',
  /** retenciones-en-el-pago AP-5.3 — a `retencion` is annulled with the `pago` of its Orden de Pago, never alone. */
  RETENCION_SE_ANULA_CON_SU_PAGO: 'AP_RETENCION_SE_ANULA_CON_SU_PAGO',
  /** ap-pagar-factura-directo — an NC or a comprobante with no pending cannot be paid. */
  COMPROBANTE_NO_PAGABLE: 'AP_COMPROBANTE_NO_PAGABLE',
  /** ap-anular-editar-factura — only a live comprobante without payments can be annulled or edited. */
  COMPROBANTE_NO_MODIFICABLE: 'AP_COMPROBANTE_NO_MODIFICABLE',
  /** ap-editar-pago — only a live Orden de Pago over exactly one invoice can be edited. */
  PAGO_NO_EDITABLE: 'AP_PAGO_NO_EDITABLE',
  /** A retention type already applied to a payment can only be deactivated, never deleted. */
  TIPO_RETENCION_EN_USO: 'AP_TIPO_RETENCION_EN_USO',
} as const;

export type ApErrorCode = (typeof AP_ERROR_CODES)[keyof typeof AP_ERROR_CODES];
